export interface ArticleSection {
  heading?: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface Article {
  id: string;
  title: string;
  categorySlug: string;
  excerpt: string;
  cover: string;
  author: string;
  date: string;
  readMinutes: number;
  tags: string[];
  featured?: boolean;
  views: number;
  keyPoints: string[];
  content: ArticleSection[];
}

export const articles: Article[] = [
  {
    id: 'inner-01',
    title: '会员体系背后的自动续费设计：一条「取消路径」的完整拆解',
    categorySlug: 'yunying-neimu',
    excerpt:
      '开会员只要两步，退订要绕五层页面。自动续费并不是技术难题，而是一套被反复打磨过的转化设计，我们从产品路径的角度把它拆开看。',
    cover:
      'https://readdy.ai/api/search-image?query=dark%20editorial%20desk%20with%20subscription%20forms%20and%20highlighted%20fine%20print%20on%20paper%2C%20red%20marker%20circling%20a%20clause%2C%20warm%20amber%20lamp%20light%2C%20no%20people%2C%20cinematic%20investigative%20mood&width=800&height=560&seq=art-inner-01&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-08-18',
    readMinutes: 8,
    tags: ['自动续费', '会员设计', '用户协议'],
    featured: true,
    views: 18420,
    keyPoints: [
      '开通与取消的路径长度差，是经过计算的设计选择，而非疏忽。',
      '「连续包月更划算」的定价结构，本质是用差额补贴推动用户选择自动续费。',
      '关键提示通常放在协议第二屏之后，且缺少醒目的到期提醒。',
    ],
    content: [
      {
        paragraphs: [
          '很多人有过类似的体验：为了看一部剧或者用一个功能，顺手开了会员，几个月后才发现银行卡每月都在被扣一笔不大不小的钱。第一反应往往是自己忘了，但如果把整个产品路径拉直来看，会发现「忘记」其实是被精心安排进流程里的。',
          '我们把主流平台的会员流程做了一次完整的路径对比，从开通入口到取消入口，逐屏记录点击次数、文案措辞与页面层级，试图回答一个问题：这到底是疏忽，还是设计。',
        ],
      },
      {
        heading: '开通两步，取消五层',
        paragraphs: [
          '在多数产品里，开通自动续费平均只需要两次点击：选择套餐、确认支付。而取消自动续费，往往要经过「我的—账号设置—会员中心—管理订阅—确认取消」等多个层级，部分产品还会在最后一步插入挽留页与优惠券。',
          '这种不对称本身并不违法，但它显著提高了用户的退出成本。当取消路径的深度超过开通路径三倍以上时，行为数据上的留存提升就变得可预期——不是用户更愿意留下，而是他们更找不到出口。',
        ],
        bullets: [
          '开通入口：首页弹窗、播放页悬浮按钮、支付成功页二次引导，至少三处入口。',
          '取消入口：通常只在账号设置的二级或三级菜单中，不做外露。',
          '挽留设计：取消前弹出折扣、赠送时长或「暂不取消」的默认选中项。',
        ],
      },
      {
        heading: '提示义务被放在了哪里',
        paragraphs: [
          '按照相关规定，经营者采用自动续费方式提供服务，应当在扣款前以显著方式提示消费者。现实中的做法是：提示确实存在，但被放在了协议的第二屏之后，字号接近正文，且不提供独立的到期提醒开关。',
          '对用户来说，更有用的做法是把「自动续费管理」当成一项常规设置去对待：开通后立刻在支付平台侧查看订阅列表，那里通常能看到所有自动扣款项目，并一键关闭，不必依赖 App 内部层层寻找。',
        ],
      },
    ],
  },
  {
    id: 'inner-02',
    title: '从零到千万日活：工具类产品的补贴节奏还原',
    categorySlug: 'yunying-neimu',
    excerpt:
      '补贴不是一直发，也不是一发放到底。把一款工具产品的增长曲线和补贴动作对齐后，能清楚看到三个阶段的不同目标。',
    cover:
      'https://readdy.ai/api/search-image?query=printed%20growth%20curve%20chart%20pinned%20on%20warm%20paper%2C%20hand%20drawn%20annotations%20and%20crossed%20out%20numbers%2C%20desk%20lamp%20light%2C%20no%20identifiable%20people%2C%20editorial%20documentary%20style&width=800&height=560&seq=art-inner-02&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-08-06',
    readMinutes: 9,
    tags: ['增长策略', '补贴', '冷启动'],
    views: 12680,
    keyPoints: [
      '补贴的第一阶段目标是留存曲线，而不是拉新数量。',
      '第二阶段转向转化率，补贴开始和具体行为绑定。',
      '第三阶段补贴退坡，同步上线新的付费点来承接。',
    ],
    content: [
      {
        paragraphs: [
          '很多关于增长的讨论停留在「烧钱换量」这个笼统判断上，但真正值得研究的，是补贴在不同阶段扮演了完全不同的角色。把一款工具类产品的公开活动和增长数据放在同一条时间轴上，轮廓会清晰很多。',
        ],
      },
      {
        heading: '第一阶段：买的是留存，不是用户',
        paragraphs: [
          '冷启动阶段最常见的动作是「邀请得奖励」，表面上看在拉新，实际考核指标是次周留存。因为此时产品尚未验证价值，单纯堆注册量的意义有限，反而会稀释数据。补贴在这里的作用是给用户一个「再来一次」的理由。',
        ],
        bullets: [
          '奖励门槛设置得极低，几乎零成本可领取。',
          '奖励有效期通常只有 3 到 7 天，逼迫用户短期内回访。',
          '核心指标从注册数切换到「完成关键行为的用户数」。',
        ],
      },
      {
        heading: '第二阶段：补贴与行为绑定',
        paragraphs: [
          '当留存曲线稳定后，补贴开始变得挑剔。同样是优惠，发放条件会绑定到具体行为，例如完成一次分享、使用某个付费功能的试用额度、或者在指定时间段内使用产品。这个阶段的目标是让用户建立使用习惯，并逐步接触付费功能。',
        ],
      },
      {
        heading: '第三阶段：退坡与承接',
        paragraphs: [
          '补贴的退坡往往不是一次性撤掉，而是同时做两件事：一方面缩小额度、提高门槛，另一方面上线新的付费点。用户感觉到的不是「补贴没了」，而是「能免费用完的部分变少了」。理解这一点，有助于你在产品改版时判断接下来会发生什么。',
        ],
      },
    ],
  },
  {
    id: 'inner-03',
    title: '大数据定价的争议：同一件商品为什么价格会不一样',
    categorySlug: 'yunying-neimu',
    excerpt:
      '新用户更便宜、老用户更贵，这种感知到底有多少来自真实的差异化定价，又有多少来自我们对随机波动的误读？',
    cover:
      'https://readdy.ai/api/search-image?query=three%20identical%20product%20tags%20with%20different%20handwritten%20prices%20on%20warm%20cardboard%2C%20soft%20overhead%20light%2C%20minimal%20editorial%20still%20life%2C%20no%20people%2C%20warm%20neutral%20tones&width=800&height=560&seq=art-inner-03&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-07-22',
    readMinutes: 7,
    tags: ['差异化定价', '消费者权益', '定价策略'],
    views: 9430,
    keyPoints: [
      '并非所有价格差异都构成「杀熟」，优惠券结构本身就是差异来源。',
      '判断的关键在于：差异依据的是否是与交易无关的个人特征。',
      '保留价格截图与下单时间，是后续投诉时最有效的证据。',
    ],
    content: [
      {
        paragraphs: [
          '「同一家店，我的价格比朋友贵」是社交平台上经久不衰的讨论。价格差异确实存在，但成因比想象中复杂，把它一概归结为针对老用户的惩罚，容易错过真正值得关注的判断标准。',
        ],
      },
      {
        heading: '价格差异的三种常见来源',
        paragraphs: [
          '第一类是优惠券与活动分配机制的差异，几乎是价格差异的绝大部分来源；第二类是渠道差异，例如 App 内、小程序与网页端的价格券不同；第三类才是与个人身份特征绑定的差异化定价，这也是监管最关注的部分。',
        ],
        bullets: [
          '优惠券分配：新人券、沉睡用户唤醒券、随机发放券，覆盖人群不同。',
          '渠道差异：不同入口的券池与活动节奏并不共享。',
          '个性化定价：依据设备、消费能力、历史订单等与商品无关的特征调整价格。',
        ],
      },
      {
        heading: '怎么判断自己遇到的是哪一种',
        paragraphs: [
          '有效的做法是对比同一时间、同一渠道、同一账号状态下的价格。如果差异来自优惠券，结算页会明确列出券的抵扣明细；如果两个账号在同一入口显示了不同基础价，且找不到任何券的说明，才更接近差异化定价的讨论范畴。',
          '遇到这种情况，建议立刻截取包含基础价、时间与账号状态的完整页面，并在下单前完成取证。这比事后回忆要有力得多。',
        ],
      },
    ],
  },
  {
    id: 'boss-01',
    title: '创始人的三次转型：公开履历之外容易被忽略的细节',
    categorySlug: 'laoban-beijing',
    excerpt:
      '同一家公司在五年内换了三次主营方向，创始人的公开访谈里只提了一次。把时间线和访谈内容对齐，能看到更完整的决策轨迹。',
    cover:
      'https://readdy.ai/api/search-image?query=timeline%20of%20faded%20photographs%20and%20documents%20spread%20on%20a%20dark%20wood%20desk%2C%20single%20warm%20lamp%2C%20no%20identifiable%20faces%2C%20editorial%20archival%20style%2C%20warm%20sepia%20tones&width=800&height=560&seq=art-boss-01&orientation=landscape',
    author: '职镜研究组',
    date: '2026-08-12',
    readMinutes: 10,
    tags: ['创始人', '战略转型', '公开履历'],
    featured: true,
    views: 15760,
    keyPoints: [
      '公开访谈往往只保留成功的那条叙述线，转型的失败尝试容易被略过。',
      '工商变更的时间点，通常比官方口径早三到六个月。',
      '判断管理层风格，看其在压力期保留了哪些团队，比看愿景更有效。',
    ],
    content: [
      {
        paragraphs: [
          '理解一家公司在做什么，往往从理解决策者开始。但公开履历通常是修剪过的版本，它展示的是结果，而不是过程中的犹豫与折返。我们把能找到的公开访谈、报道与工商变更信息放到同一条时间轴上，试图还原更完整的轨迹。',
        ],
      },
      {
        heading: '时间线比叙述更诚实',
        paragraphs: [
          '一个反复出现的现象是：管理层在访谈中描述战略方向时，使用的时点通常晚于实际的业务调整。也就是说，当对外宣布「全面转向某方向」时，内部的组织与资源调整可能已经进行了半年。工商信息中的经营范围变更、分支机构注销与股东调整，往往是更早的信号。',
        ],
        bullets: [
          '经营范围与股权变更：通常先于对外战略发布出现。',
          '招聘结构变化：职位描述的侧重，反映真实的资源倾斜方向。',
          '高管离职节奏：核心岗位的更替往往与业务收缩同步。',
        ],
      },
      {
        heading: '在压力期保留谁',
        paragraphs: [
          '判断管理层的真实优先级，一个朴素但有效的方法是看其在收缩期保留了哪些岗位。如果被保留的是与短期收入直接相关的团队，而长期投入型团队被优先削减，那么「长期主义」的说法就需要打上折扣。',
        ],
      },
      {
        heading: '如何自行核对',
        paragraphs: [
          '这些信息大多是公开的。你可以通过官方公告、权威媒体的早期报道、以及公开的企业信息平台交叉核对。需要提醒的是，公开信息可能存在滞后与缺漏，任何单一来源都不足以形成结论，交叉验证是必要的。',
        ],
      },
    ],
  },
  {
    id: 'boss-02',
    title: '管理层的资本关系网：一份可查证的股权变更梳理方法',
    categorySlug: 'laoban-beijing',
    excerpt:
      '不看八卦，只看结构。谁在什么时候增持、谁在什么时候退出，这些变更记录能帮你看懂一家公司的实际控制关系。',
    cover:
      'https://readdy.ai/api/search-image?query=network%20diagram%20of%20connected%20nodes%20drawn%20in%20ink%20on%20warm%20paper%2C%20magnifying%20glass%20resting%20beside%20it%2C%20desk%20lamp%20light%2C%20no%20people%2C%20editorial%20research%20aesthetic&width=800&height=560&seq=art-boss-02&orientation=landscape',
    author: '职镜研究组',
    date: '2026-07-30',
    readMinutes: 9,
    tags: ['股权结构', '实际控制人', '公开信息'],
    views: 8120,
    keyPoints: [
      '股权比例之外，表决权与一致行动安排更能决定谁说了算。',
      '频繁的股权质押，往往与企业现金流状况相关。',
      '核对时以官方登记信息为准，谨慎对待二手转述。',
    ],
    content: [
      {
        paragraphs: [
          '关于管理层背景的讨论很容易滑向私人话题，但真正对求职者与外部合作方有价值的，是结构性的信息：谁是实际控制人、决策权如何分配、近几年的资本动作指向什么。这些内容大多可以通过公开渠道核实。',
        ],
      },
      {
        heading: '三个值得关注的结构指标',
        paragraphs: [
          '第一是表决权安排，某些结构下持股比例不高的一方仍然掌握决策权；第二是股权质押情况，高比例质押通常意味着股东存在较大的资金压力；第三是近三年的股权转让与增资节奏，密集的变更往往伴随业务结构的调整。',
        ],
        bullets: [
          '表决权：关注是否存在一致行动协议或特殊表决权安排。',
          '质押：比例过高需要结合企业现金流状况一起看。',
          '变更节奏：频繁的股权转让常与融资或业务重组同步。',
        ],
      },
      {
        heading: '把信息当成线索而不是结论',
        paragraphs: [
          '需要强调的是，股权动作本身是中性的，它既可能指向危机，也可能只是常规的架构调整。有价值的做法是把它当作线索，再去寻找能够印证或推翻它的其他公开信息，而不是直接下判断。',
        ],
      },
    ],
  },
  {
    id: 'staff-01',
    title: '入职三个月真实记录：招聘承诺与落地执行的差距在哪',
    categorySlug: 'yuangong-baoliao',
    excerpt:
      '面试时说的双休、年终、涨薪窗口，入职后都变了样。一位运营岗员工的三个月记录，把差异逐条列了出来。',
    cover:
      'https://readdy.ai/api/search-image?query=open%20notebook%20with%20two%20columns%20of%20handwritten%20notes%20comparing%20promises%20and%20reality%2C%20warm%20desk%20light%2C%20no%20identifiable%20people%2C%20editorial%20documentary%20style&width=800&height=560&seq=art-staff-01&orientation=landscape',
    author: '匿名投稿 · 已核实',
    date: '2026-08-20',
    readMinutes: 7,
    tags: ['招聘承诺', '试用期', '工作体验'],
    views: 11240,
    keyPoints: [
      '口头承诺尽量落到 offer 或劳动合同中，否则很难主张。',
      '制度变更通常以「新老办法」过渡，留意过渡期的书面通知。',
      '保留入职时的岗位说明与沟通记录，是后续判断依据。',
    ],
    content: [
      {
        paragraphs: [
          '本文整理自一位运营岗员工的投稿。为保护当事人，涉及部门与姓名信息均已做脱敏处理。之所以把这份记录整理出来，是因为它提到的差异点具有很强的普遍性。',
        ],
      },
      {
        heading: '面试阶段与入职之后',
        paragraphs: [
          '投稿者提到，面试时沟通的是弹性上下班、季度绩效与固定的年度调薪窗口。正式入职后，弹性上下班被要求改为固定时间打卡；季度绩效的实际发放比例与原始说明存在差距；调薪窗口则在第二季度以「业务调整」为由暂缓。',
        ],
        bullets: [
          '考勤：面试所述弹性机制在入职首月被取消。',
          '绩效：计算基数的口径与入职说明不一致。',
          '调薪：原有时间窗口延后，且未给出明确重启时间。',
        ],
      },
      {
        heading: '可以提前做的准备',
        paragraphs: [
          '很多差异并非始于恶意，而是源于招聘与用工部门之间的信息不同步，但对个人来说，结果是一样的。务实的做法是把关键承诺写进 offer 或劳动合同；如果对方以「公司统一模板」为由拒绝补充，至少保留清晰的书面沟通记录。',
        ],
      },
    ],
  },
  {
    id: 'staff-02',
    title: '一线员工眼中为什么一个需求要走十一道审批',
    categorySlug: 'yuangong-baoliao',
    excerpt:
      '流程越长，责任越分散。一位产品岗员工讲述了他所在团队审批链的由来，以及它如何一步步长成今天的样子。',
    cover:
      'https://readdy.ai/api/search-image?query=long%20flowchart%20of%20approval%20boxes%20drawn%20on%20a%20whiteboard%2C%20warm%20evening%20office%20light%2C%20no%20identifiable%20people%2C%20editorial%20documentary%20style%2C%20muted%20warm%20palette&width=800&height=560&seq=art-staff-02&orientation=landscape',
    author: '匿名投稿 · 已核实',
    date: '2026-08-02',
    readMinutes: 6,
    tags: ['内部流程', '协作效率', '组织管理'],
    views: 7960,
    keyPoints: [
      '审批节点通常是历史事故的产物，而非最初的设计。',
      '流程膨胀的根源是责任归属不清，而不是员工不信任。',
      '推动简化需要先解决责任兜底问题，否则节点只会越加越多。',
    ],
    content: [
      {
        paragraphs: [
          '当一个人需要经过十一个审批节点才能上线一处文案修改时，问题通常不在审批者本身。这份投稿提供了一个从历史沿革角度理解流程膨胀的视角。',
        ],
      },
      {
        heading: '每一个节点都有它的来历',
        paragraphs: [
          '投稿者梳理后发现，大部分审批节点都能追溯到过去某个具体事故：一次口径错误、一次合规风险、一次客户投诉。每出一次问题，就增加一个复核环节，久而久之形成了今天的链条。',
        ],
        bullets: [
          '风险类节点：源于历史合规事件。',
          '复核类节点：源于对外口径不一致。',
          '签批类节点：源于跨部门责任划分不清。',
        ],
      },
      {
        heading: '为什么简化这么难',
        paragraphs: [
          '简化流程意味着有人要承担原本被分摊的风险。在责任机制没有改变之前，任何一次简化尝试都可能在出现问题后被迅速回退，甚至叠加更多节点。这也解释了为什么「提效」的口号年年有，流程却越来越长。',
        ],
      },
    ],
  },
  {
    id: 'staff-03',
    title: '试用期转正评估：标准模糊时，怎样保护自己',
    categorySlug: 'yuangong-baoliao',
    excerpt:
      '转正被拒但理由始终含糊，是很多人遇到过的情况。与其事后争论，不如在入职初期就把评估依据固定下来。',
    cover:
      'https://readdy.ai/api/search-image?query=blank%20evaluation%20form%20with%20empty%20checkbox%20rows%20on%20a%20warm%20desk%2C%20pen%20beside%20it%2C%20soft%20directional%20light%2C%20no%20identifiable%20people%2C%20editorial%20still%20life&width=800&height=560&seq=art-staff-03&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-07-18',
    readMinutes: 6,
    tags: ['试用期', '转正评估', '劳动权益'],
    views: 6890,
    keyPoints: [
      '录用条件需要在入职时明确告知，否则解除的举证基础薄弱。',
      '主动确认考核标准，并把确认过程留在书面渠道。',
      '被通知不通过时，先要求书面理由，再决定下一步。',
    ],
    content: [
      {
        paragraphs: [
          '试用期并不是一段可以随意处置的时间。法律对试用期的期限、工资下限与解除条件都有约束，问题在于很多公司的评估标准过于含糊，导致争议发生时双方各执一词。',
        ],
      },
      {
        heading: '把标准固定下来',
        paragraphs: [
          '入职第一个月内，可以主动向直属上级确认转正评估的维度与权重，并通过邮件或内部办公工具留下记录。这并非不信任，而是让双方对预期有共同的理解。如果对方无法给出明确标准，这本身就是一个值得注意的信号。',
        ],
        bullets: [
          '确认评估维度：产出指标、协作表现、合规要求分别是什么。',
          '确认时间节点：中期回顾与正式评估分别在什么时候。',
          '确认反馈渠道：过程中如果发现偏差，通过哪个渠道沟通。',
        ],
      },
      {
        heading: '被通知不通过之后',
        paragraphs: [
          '第一步是要求对方提供书面的评估结论与依据，而不是当场争论。拿到书面说明后，再对照入职时的岗位说明判断其是否成立。如果公司无法提供录用条件的明确告知记录，解除行为的合法性就存在讨论空间。',
        ],
      },
    ],
  },
  {
    id: 'office-01',
    title: '工位、通勤与加班餐：办公体验的细节比福利海报更真实',
    categorySlug: 'bangong-huanjing',
    excerpt:
      '招聘页面会写「扁平开放」，但不会告诉你工位之间只有半米。办公体验由大量琐碎细节构成，这些细节才是日常。',
    cover:
      'https://readdy.ai/api/search-image?query=close%20up%20of%20a%20crowded%20open%20plan%20desk%20with%20personal%20items%20and%20monitor%2C%20warm%20ceiling%20light%2C%20no%20identifiable%20people%2C%20editorial%20documentary%20photography%2C%20warm%20muted%20tones&width=800&height=560&seq=art-office-01&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-08-15',
    readMinutes: 6,
    tags: ['办公环境', '通勤', '工作强度'],
    views: 7210,
    keyPoints: [
      '工位密度直接影响专注程度，也反映公司的成本取向。',
      '通勤时间应计入真实工作时长，这是最容易被忽略的成本。',
      '加班后的配套安排，往往比口头倡导更能说明问题。',
    ],
    content: [
      {
        paragraphs: [
          '办公环境之所以值得单独观察，是因为它由高频、琐碎但持续作用的因素组成。这些因素很少出现在招聘宣传中，却决定了你每天八小时以上的真实体感。',
        ],
      },
      {
        heading: '三个可量化的观察点',
        paragraphs: [
          '第一是工位密度，人均面积越低，被打断的频率越高；第二是通勤时间，单程超过一小时会显著压缩可支配时间；第三是集中办公与会议的配比，连续会议超过三小时，深度工作的可能性基本消失。',
        ],
        bullets: [
          '工位密度：观察走道宽度与隔断高度。',
          '通勤成本：把单程时间乘以每个工作日，换算成月度小时数。',
          '会议配比：留意是否存在无明确议程的例行会议。',
        ],
      },
      {
        heading: '细节里的一致性',
        paragraphs: [
          '一个实用的判断方法是看「说」和「做」是否一致。如果公司强调员工健康，那么加班餐是否有营养、是否有弹性下班安排、会议室是否预留了休息空间，这些具体做法会比墙上的标语更可信。',
        ],
      },
    ],
  },
  {
    id: 'office-02',
    title: '开放式办公的另一面：工位密度、会议噪声与专注时间',
    categorySlug: 'bangong-huanjing',
    excerpt:
      '开放式布局节约了成本，也把噪声变成了共享资源。它如何影响专注与协作，其实有比较明确的规律。',
    cover:
      'https://readdy.ai/api/search-image?query=wide%20open%20plan%20office%20with%20rows%20of%20empty%20desks%20and%20glass%20meeting%20rooms%2C%20warm%20afternoon%20light%2C%20no%20identifiable%20people%2C%20editorial%20architectural%20photography&width=800&height=560&seq=art-office-02&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-07-26',
    readMinutes: 5,
    tags: ['开放式办公', '专注力', '协作'],
    views: 5420,
    keyPoints: [
      '开放式布局降低沟通门槛，同时抬高被中断的频率。',
      '缺乏安静区，会使需要深度思考的工作被迫转移到下班后。',
      '工位安排方式往往透露团队在组织中的实际优先级。',
    ],
    content: [
      {
        paragraphs: [
          '开放式办公一度被视为高效协作的象征，但它同时把环境噪声变成了所有人共享的资源。当一个人的电话会议能够影响周围十几个人时，协作的成本其实被转移了。',
        ],
      },
      {
        heading: '被中断的代价',
        paragraphs: [
          '注意力被中断后重新进入状态通常需要十几分钟。在密度较高的办公区，打断可能每小时发生数次，这意味着需要连续思考的任务实际上很难在正常工时内完成，于是被自然推到了加班时段。',
        ],
        bullets: [
          '设置明确的安静区，并说明使用规则。',
          '把需要长时间专注的任务集中在固定的免打扰时段。',
          '会议尽量使用隔音空间，或改为文字异步沟通。',
        ],
      },
      {
        heading: '座位本身的信息',
        paragraphs: [
          '工位的位置并非随机分配。靠近核心通道的位置通常留给高频协作岗位，邻近管理层的座位往往与汇报关系对应。观察座位分布，有时比看组织架构图更快理解真实的团队结构。',
        ],
      },
    ],
  },
  {
    id: 'layoff-01',
    title: '组织调整的三十天：从「优化」通知到最终离职',
    categorySlug: 'caiyuan-neimu',
    excerpt:
      '通知、约谈、签署、交接，每一步都有固定的节奏。提前了解这套流程，能帮你在被动局面里保留主动权。',
    cover:
      'https://readdy.ai/api/search-image?query=cardboard%20moving%20box%20with%20personal%20belongings%20on%20an%20empty%20desk%2C%20warm%20dim%20lighting%2C%20no%20identifiable%20people%2C%20editorial%20melancholic%20still%20life%2C%20warm%20amber%20tone&width=800&height=560&seq=art-layoff-01&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-08-22',
    readMinutes: 9,
    tags: ['裁员', '协商解除', '经济补偿'],
    featured: true,
    views: 20350,
    keyPoints: [
      '第一轮通知多为口头，此时不必当场做任何承诺。',
      '补偿方案要落到书面，口头数字不具备约束力。',
      '交接完成前，注意保留考勤与沟通记录。',
    ],
    content: [
      {
        paragraphs: [
          '组织调整往往遵循一套相对固定的流程，理解它的节奏，可以让你在信息不对称的情况下做出更从容的判断。以下流程整理自多份投稿与公开案例，个体差异可能存在，仅作参考。',
        ],
      },
      {
        heading: '第一阶段：通知与试探',
        paragraphs: [
          '最初的沟通通常由直属上级或 HR 以口头形式进行，措辞倾向于使用「优化」「调整」「给予更多选择」等较为温和的表达。这个阶段的核心目的是观察你的反应，因此不必当场表态，也不必签署任何文件。你有权要求将沟通内容以书面形式确认。',
        ],
        bullets: [
          '保持冷静，记录下来沟通的时间、参与人与主要说法。',
          '明确表示需要时间考虑，可以要求提供书面方案。',
          '不要在被围谈的氛围下做出任何口头承诺。',
        ],
      },
      {
        heading: '第二阶段：方案与协商',
        paragraphs: [
          '补偿方案进入协商阶段，核心变量包括补偿月数、计算基数、社保与公积金的截止月份、未休年假的折算方式，以及竞业与离职证明的表述。这些细节的差异，最终可能影响数万元。',
          '需要特别留意协议中出现「自愿离职」「个人原因」等表述。这类措辞可能影响后续主张经济补偿的权利，在确认补偿到账安排之前，不建议签署。',
        ],
      },
      {
        heading: '第三阶段：交接与离场',
        paragraphs: [
          '交接阶段容易被忽略的是证据留存。考勤记录、加班审批、绩效结果与工资流水，如果平时没有备份，在权限被收回后就很难再获取。建议在交接开始前完成整理。',
          '离职证明应写明在职起止时间与岗位，如果对方拒绝出具或使用含糊表述，可以明确要求按实际情况书写，这是你的正当权利。',
        ],
      },
    ],
  },
  {
    id: 'layoff-02',
    title: '离职补偿怎么算：N、N+1 与「协商一致」之间的差别',
    categorySlug: 'caiyuan-neimu',
    excerpt:
      '很多人只知道 N 和 N+1 这两个说法，却不清楚它们各自适用的情形，结果在协商时失去了判断依据。',
    cover:
      'https://readdy.ai/api/search-image?query=calculator%20and%20severance%20agreement%20documents%20on%20warm%20wooden%20desk%20with%20pen%2C%20soft%20directional%20light%2C%20no%20identifiable%20people%2C%20editorial%20flat%20lay%20photography&width=800&height=560&seq=art-layoff-02&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-08-09',
    readMinutes: 8,
    tags: ['经济补偿', '协商解除', '劳动法规'],
    views: 16890,
    keyPoints: [
      '补偿月数与计算基数是两个独立变量，都要确认清楚。',
      '计算基数通常按解除前十二个月平均工资，且有上限规定。',
      '协商一致解除也需要支付经济补偿，并非「自愿」就没有。',
    ],
    content: [
      {
        paragraphs: [
          '关于离职补偿的讨论里，最容易被简化的就是「N」和「N+1」。它们并不是可以随意挑选的两个选项，而是对应不同的法律情形。理清这一点，协商时才有判断基准。',
        ],
      },
      {
        heading: '两种常见情形',
        paragraphs: [
          '一种是用人单位依据法定情形解除劳动合同，需要提前三十日书面通知，未提前通知的，需额外支付一个月工资，也就是常说的「+1」。另一种是双方协商一致解除，同样需要支付经济补偿，但通常不涉及代通知金。',
        ],
        bullets: [
          '经济补偿：按工作年限计算，每满一年支付一个月工资。',
          '代通知金：未提前三十日通知时额外支付的一个月工资。',
          '计算基数：一般为解除前十二个月的平均工资。',
        ],
      },
      {
        heading: '容易被忽略的几个点',
        paragraphs: [
          '第一，工作年限计算中包含不满一年但超过六个月的部分，应按一年计算；第二，月工资高于当地社会平均工资三倍的，补偿基数与年限存在上限规定；第三，未休年假、当月工资与社保应单独结清，不应被混入补偿总额中含糊处理。',
          '把这些项目逐条列出并对齐书面协议，是协商阶段最实用的准备工作。任何仅以口头数字确认的方案，都建议要求落实到文本。',
        ],
      },
    ],
  },
  {
    id: 'salary-01',
    title: '绩效被打低之后：工资条上消失的那部分去了哪',
    categorySlug: 'xinzi-jiufen',
    excerpt:
      '同样的岗位，这个月拿到的钱少了一截。绩效被调整往往没有明确解释，但工资条上的科目其实藏着线索。',
    cover:
      'https://readdy.ai/api/search-image?query=salary%20slip%20and%20payroll%20documents%20with%20highlighted%20deduction%20lines%2C%20warm%20desk%20lamp%2C%20no%20identifiable%20people%2C%20editorial%20close%20up%20photography%2C%20warm%20neutral%20palette&width=800&height=560&seq=art-salary-01&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-08-05',
    readMinutes: 7,
    tags: ['绩效工资', '工资条', '劳动权益'],
    views: 10430,
    keyPoints: [
      '把工资条按月留档，是比较不同科目变化的前提。',
      '绩效扣减需要有制度依据与考核结果支撑。',
      '沟通时优先索要书面说明，而非只做口头交涉。',
    ],
    content: [
      {
        paragraphs: [
          '工资条在很多人手里只是一串数字，但它其实是理解薪酬结构最直接的文本。当收入出现波动时，第一时间保存工资条，往往比事后回忆有效得多。',
        ],
      },
      {
        heading: '先把科目拆开',
        paragraphs: [
          '典型工资条包含基本工资、岗位工资、绩效工资、各类补贴、加班费与扣款项目。出现减少时，先定位是哪一项发生了变化。是绩效工资被下调，还是加班费未计入，抑或是补贴被取消，不同科目的处理方式完全不同。',
        ],
        bullets: [
          '绩效工资：需要有考核制度与考核结果作为依据。',
          '加班费：与考勤记录是否完整直接相关。',
          '各类补贴：留意是否在制度中被单方面调整。',
        ],
      },
      {
        heading: '留痕的重要性',
        paragraphs: [
          '无论是绩效沟通还是薪酬调整，尽量通过公司邮箱或内部办公工具进行，并保留原始记录。如果对方习惯口头沟通，可以在沟通后以文字形式复述确认，例如「关于今天的绩效说明，我理解为……如有偏差请指正」。这种方式既不激化矛盾，也能形成有效记录。',
        ],
      },
    ],
  },
  {
    id: 'salary-02',
    title: '加班费与调休：劳动仲裁里最常见的三类争议焦点',
    categorySlug: 'xinzi-jiufen',
    excerpt:
      '加班到底算不算、调休能不能折抵、举证责任在谁，这三个问题几乎出现在每一场加班相关的争议里。',
    cover:
      'https://readdy.ai/api/search-image?query=attendance%20records%20and%20timesheet%20printouts%20stacked%20on%20a%20warm%20desk%20with%20a%20clock%20nearby%2C%20soft%20light%2C%20no%20identifiable%20people%2C%20editorial%20documentary%20still%20life&width=800&height=560&seq=art-salary-02&orientation=landscape',
    author: '职镜编辑部',
    date: '2026-07-14',
    readMinutes: 8,
    tags: ['加班费', '调休', '劳动仲裁'],
    views: 8890,
    keyPoints: [
      '打卡记录与审批流程，是判断加班是否成立的主要证据。',
      '工作日加班通常不能以调休替代加班费。',
      '申请仲裁有法定时效，越早固定证据越有利。',
    ],
    content: [
      {
        paragraphs: [
          '加班相关的争议之所以常见，是因为它同时涉及事实认定与制度适用两个层面。以下三类焦点，几乎覆盖了绝大多数实际案例。',
        ],
      },
      {
        heading: '焦点一：加班事实如何认定',
        paragraphs: [
          '单纯的「走得晚」不一定构成法律意义上的加班，关键在于是否有工作安排。打卡记录、任务群消息、审批流程与产出文件的时间戳，都是认定加班事实的重要材料。因此，平时在内部系统中规范走审批流程，本身就是一种保护。',
        ],
        bullets: [
          '考勤记录：导出并保存原始打卡数据。',
          '沟通记录：保留工作安排与任务下达的时间信息。',
          '产出文件：保留带有时间戳的文档与提交记录。',
        ],
      },
      {
        heading: '焦点二：调休能否替代加班费',
        paragraphs: [
          '一般而言，休息日加班可以通过安排调休来抵偿；工作日延长工作时间的，通常不能以调休替代加班费。把这两类情形混在一起处理，是实践中常见的模糊说法，需要分开核对。',
        ],
      },
      {
        heading: '焦点三：举证责任与时效',
        paragraphs: [
          '考勤记录由用人单位掌握，在争议中通常由单位承担相应的提供义务。但申请仲裁有时效限制，且越早固定证据越有利。与其等到离职后集中处理，不如在日常就养成留存关键记录的习惯。',
        ],
      },
    ],
  },
];